import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PartyPopper, HandHeart, Check, Trash2 } from "lucide-react";

/**
 * Homeowner-facing RSVP form for the Labor Day Block Party.
 *
 * Renders inline on the portal dashboard. Fetches the event details + the
 * current user's saved RSVP (if any) so members can update or cancel later.
 * Attendee_count of 0 means "not attending". Volunteer roles are optional.
 */
export default function LaborDayRsvpForm() {
  const [event, setEvent] = useState(null);
  const [existing, setExisting] = useState(null); // null = none saved, object = saved rsvp
  const [attendees, setAttendees] = useState(2);
  const [roles, setRoles] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/events/labor-day"),
      api.get("/portal/events/labor-day/my-rsvp").catch(() => ({ data: null })),
    ]).then(([ev, my]) => {
      if (cancelled) return;
      setEvent(ev.data);
      if (my.data) {
        setExisting(my.data);
        setAttendees(my.data.attendee_count ?? 0);
        setRoles(my.data.volunteer_roles || []);
        setNote(my.data.other_note || "");
      }
    });
    return () => { cancelled = true; };
  }, []);

  const toggleRole = (key) => {
    setRoles((rs) => rs.includes(key) ? rs.filter((r) => r !== key) : [...rs, key]);
  };

  if (!event) return null;
  // Hide once the event has passed
  try {
    const eventDate = new Date(event.date + "T23:59:59");
    if (Date.now() > eventDate.getTime()) return null;
  } catch { /* ignore */ }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/portal/events/labor-day/rsvp", {
        attendee_count: Number(attendees) || 0,
        volunteer_roles: roles,
        other_note: note,
      });
      setExisting(r.data);
      toast.success(existing ? "RSVP updated" : "You're on the list!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save RSVP");
    } finally {
      setBusy(false);
    }
  };

  const cancelRsvp = async () => {
    if (!window.confirm("Cancel your Labor Day RSVP?")) return;
    setBusy(true);
    try {
      await api.delete("/portal/events/labor-day/rsvp");
      setExisting(null);
      setAttendees(2);
      setRoles([]);
      setNote("");
      toast.success("RSVP cancelled");
    } catch { toast.error("Could not cancel"); }
    finally { setBusy(false); }
  };

  return (
    <Card
      id="labor-day-rsvp-form"
      data-testid="labor-day-rsvp-form"
      className="rounded-2xl border-[#D5CFC4] mb-10"
    >
      <CardContent className="p-6 md:p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-[#EADCC8] flex items-center justify-center shrink-0">
            <PartyPopper className="w-6 h-6 text-[#C86650]" />
          </div>
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-1">RSVP · {event.title}</p>
            <h3 className="font-serif text-2xl text-[#1A2421]">Count me in for {event.date_label}</h3>
            <p className="text-sm text-[#5C6A64] mt-1">
              We need an accurate food count. RSVPing takes 20 seconds.
              {existing && (
                <Badge className="ml-2 bg-[#2C4C3B] text-white text-[10px]" data-testid="rsvp-saved-badge">
                  <Check className="w-3 h-3 mr-1" /> Saved
                </Badge>
              )}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">How many will attend? (0 = not this year)</label>
            <Input
              data-testid="rsvp-attendees"
              type="number"
              min={0}
              max={20}
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              className="mt-1 rounded-xl w-32"
            />
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-[#5C6A64] mb-3 flex items-center gap-2">
              <HandHeart className="w-4 h-4 text-[#C86650]" />
              Volunteer to help (optional)
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {event.volunteer_roles.map((r) => {
                const selected = roles.includes(r.key);
                return (
                  <label
                    key={r.key}
                    data-testid={`rsvp-role-${r.key}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selected ? "bg-[#FDF9F1] border-[#C86650]" : "bg-white border-[#D5CFC4] hover:border-[#2C4C3B]"}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleRole(r.key)}
                      className="w-4 h-4 accent-[#C86650]"
                    />
                    <span className="text-sm text-[#1A2421]">{r.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {roles.includes("other") && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Tell us how you'd like to help</label>
              <Textarea
                data-testid="rsvp-other-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Bring extra chairs, help with music, take photos…"
                className="mt-1 rounded-xl"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              type="submit"
              disabled={busy}
              data-testid="rsvp-submit"
              className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-11 px-6"
            >
              {busy ? "Saving…" : existing ? "Update RSVP" : "Submit RSVP"}
            </Button>
            {existing && (
              <Button
                type="button"
                variant="outline"
                onClick={cancelRsvp}
                disabled={busy}
                data-testid="rsvp-cancel"
                className="rounded-full border-[#D5CFC4] text-[#5C6A64] h-11 px-6"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Cancel RSVP
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
